import React, { Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

import { AuthProvider, useAuth } from './hooks/useAuth';

const Landing = React.lazy(() => import('./pages/Landing'));
const GamePage = React.lazy(() => import('./pages/GamePage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const DayDetail = React.lazy(() => import('./pages/DayDetail'));
const ProgressPage = React.lazy(() => import('./pages/Progress'));
const QuickRef = React.lazy(() => import('./pages/QuickRef'));
const Quiz = React.lazy(() => import('./pages/Quiz'));
const AuthPage = React.lazy(() => import('./pages/AuthPage'));

function UserBadge() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <button className="nav-auth-btn" onClick={() => navigate('/login')}>
        Sign In
      </button>
    );
  }

  return (
    <div className="nav-user">
      <span className="nav-username">🎖️ {user.username}</span>
      <button className="nav-logout" onClick={logout}>Logout</button>
    </div>
  );
}

function StudyNavbar() {
  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/study" className="nav-logo">
          <div className="nav-logo-icon">🛡️</div>
          Study
        </Link>
        <div className="nav-links">
          <Link to="/">Main Menu</Link>
          <Link to="/study">Dashboard</Link>
          <Link to="/study/progress">Progress</Link>
          <Link to="/study/quiz">Quiz</Link>
          <Link to="/study/quickref">Quick Ref</Link>
        </div>
        <UserBadge />
      </div>
    </nav>
  );
}

function SiteMusic() {
  const location = useLocation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const unlockedRef = useRef(false);

  useEffect(() => {
    const unlock = () => {
      unlockedRef.current = true;
    };
    window.addEventListener('click', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.14;

    const noMusic =
      location.pathname.startsWith('/game') ||
      location.pathname === '/' ||
      location.pathname.startsWith('/study/day/') ||
      location.pathname === '/study/quiz';

    if (noMusic) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      return;
    }

    if (!audio.getAttribute('src')) {
      audio.src = '/game/audio/menu-lofi.mp3';
      audio.load();
    }

    if (unlockedRef.current) {
      audio.play().catch(() => {});
    }
  }, [location.pathname]);

  return <audio ref={audioRef} loop preload="none" style={{ display: 'none' }} />;
}

function SEOManager() {
  const location = useLocation();
  const origin = 'https://securityplusprep.com';

  useEffect(() => {
    const pathname = location.pathname;
    const canonical = `${origin}${pathname}`;

    const metaByRoute: Record<string, { title: string; description: string; type?: string; ld?: Record<string, unknown> }> = {
      '/': {
        title: 'Security+ SY0-701 Prep | Study Plan, Quiz & Cybersecurity RPG',
        description: 'Free Security+ SY0-701 prep with a 45-day study plan, domain quizzes, quick-reference cards, and SecureDefense, a cybersecurity RPG study game.',
        ld: { '@context': 'https://schema.org', '@type': 'WebSite', name: 'SecurityPlusPrep', url: origin }
      },
      '/study': {
        title: '45-Day Security+ Study Plan | SecurityPlusPrep',
        description: 'Follow a structured 45-day Security+ SY0-701 study plan with daily topics and progress tracking.',
        type: 'article'
      },
      '/study/quiz': {
        title: 'Security+ Domain Quiz Mode | SecurityPlusPrep',
        description: 'Practice SY0-701 domain quizzes with randomized questions, scoring, and progress tracking.',
        type: 'article'
      },
      '/study/quickref': {
        title: 'Security+ Quick Reference Cards | SecurityPlusPrep',
        description: 'Review Security+ SY0-701 concepts fast with concise quick-reference cards by domain.',
        type: 'article'
      },
      '/study/progress': {
        title: 'Security+ Study Progress Tracker | SecurityPlusPrep',
        description: 'Track completed days, quiz performance, and momentum across your Security+ SY0-701 prep.',
        type: 'article'
      },
      '/game': {
        title: 'SecureDefense Cybersecurity RPG | SecurityPlusPrep',
        description: 'Play SecureDefense, a post-apocalypse cybersecurity RPG that reinforces Security+ SY0-701 concepts through gameplay.',
        type: 'website',
        ld: { '@context': 'https://schema.org', '@type': 'VideoGame', name: 'SecureDefense', url: `${origin}/game`, genre: 'Educational' }
      },
      '/login': {
        title: 'Sign In | SecurityPlusPrep',
        description: 'Sign in or create an account to save quiz scores, study progress, and game progress.'
      }
    };

    const matchedRoute = Object.keys(metaByRoute)
      .sort((a, b) => b.length - a.length)
      .find((route) => pathname === route || pathname.startsWith(`${route}/`));
    const routeConfig = metaByRoute[matchedRoute || '/'];

    document.title = routeConfig.title;

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        if (prop) el.setAttribute('property', name);
        else el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    setMeta('description', routeConfig.description);
    setMeta('og:title', routeConfig.title, true);
    setMeta('og:description', routeConfig.description, true);
    setMeta('og:type', routeConfig.type || 'website', true);
    setMeta('og:url', canonical, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', routeConfig.title);
    setMeta('twitter:description', routeConfig.description);

    const existingLd = document.getElementById('route-ld-json');
    if (existingLd) existingLd.remove();
    if (routeConfig.ld) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'route-ld-json';
      script.textContent = JSON.stringify(routeConfig.ld);
      document.head.appendChild(script);
    }
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <SEOManager />
          <SiteMusic />
          <StudyNavbarWrapper />
          <Suspense fallback={<div style={{ padding: '24px', color: '#cbd5e1' }}>Loading...</div>}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/game" element={<GamePage />} />
              <Route path="/study" element={<StudyLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="day/:day" element={<DayDetail />} />
                <Route path="progress" element={<ProgressPage />} />
                <Route path="quiz" element={<Quiz />} />
                <Route path="quickref" element={<QuickRef />} />
              </Route>
            </Routes>
          </Suspense>
        </div>
      </Router>
    </AuthProvider>
  );
}

function StudyNavbarWrapper() {
  const location = useLocation();
  if (!location.pathname.startsWith('/study')) return null;
  return <StudyNavbar />;
}

export default App;

const StudyLayout: React.FC = () => (
  <>
    <main className="main-content">
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="day/:day" element={<DayDetail />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="quiz" element={<Quiz />} />
        <Route path="quickref" element={<QuickRef />} />
      </Routes>
    </main>
    <footer className="footer">
      <p>Security+ SY0-701 — 45 Day Study Plan</p>
    </footer>
  </>
);
